if (!customElements.get('fp-sticker-configurator')) {
  customElements.define('fp-sticker-configurator', class extends HTMLElement {
    connectedCallback() {
      this.product = JSON.parse(this.querySelector('[data-product-json]').textContent);
      this.form = this.querySelector('form');
      this.variantInput = this.querySelector('[name="id"]');
      this.price = this.querySelector('[data-price]');
      this.addButton = this.querySelector('[data-add-button]');
      this.preview = this.querySelector('[data-preview-art]');
      this.previewStart = this.querySelector('[data-preview-start]');
      this.previewImage = this.querySelector('[data-preview-image]');
      this.previewPlaceholder = this.querySelector('[data-preview-placeholder]');
      this.previewStage = this.querySelector('[data-preview-launch-vault]');
      this.previewDocument = this.querySelector('[data-preview-document]');
      this.previewDocumentType = this.querySelector('[data-preview-document-type]');
      this.previewDocumentName = this.querySelector('[data-preview-document-name]');
      this.previewDocumentStatus = this.querySelector('[data-preview-document-status]');
      this.previewCopy = this.querySelector('[data-preview-upload-copy]');
      this.previewFileName = this.querySelector('[data-preview-file-name]');
      this.isGlowProduct = this.dataset.productHandle === 'glow-stickers';
      this.isCrystalProduct = this.dataset.productHandle === 'crystal-holographic';
      this.isGlitterProduct = this.dataset.productHandle === 'glitter-stickers';
      this.isHolographicProduct = this.dataset.productHandle === 'holographic-stickers';
      this.isEggshellProduct = this.dataset.productHandle === 'clear-stickers';
      this.previewBlobUrl = '';
      this.previewRequest = 0;
      this.vaultPreviewKey = '';
      this.vaultPreviewSource = '';
      this.shapeInput = this.querySelector('[name="properties[Shape]"]');
      this.borderInput = this.querySelector('[name="properties[Cloud border]"]');
      this.borderSizeInput = this.querySelector('[data-border-size]');
      this.borderSizeOutput = this.querySelector('[data-border-size-output]');
      this.borderSizeProperty = this.querySelector('[data-border-size-property]');
      this.outlineProportionProperty = this.querySelector('[data-outline-proportion-property]');
      this.contourMethodProperty = this.querySelector('[data-contour-method-property]');
      this.outlineReferenceName = this.querySelector('[data-outline-reference-name]');
      this.outlineReferenceVersion = this.querySelector('[data-outline-reference-version]');
      this.contourStatus = this.querySelector('[data-contour-status]');
      this.borderSliderWrap = this.querySelector('[data-border-slider-wrap]');
      this.previewZoomOutput = this.querySelector('[data-preview-zoom-output]');
      this.contourBaseSource = '';
      this.contourBaseOwned = false;
      this.contourFileName = '';
      this.contourProofBlob = null;
      this.contourProofSignature = '';
      this.contourProofUpload = null;
      this.contourRenderRequest = 0;
      this.contourSubmitBypass = false;
      this.previewScale = 1;
      const selected = this.product.variants.find((variant) => String(variant.id) === String(this.variantInput.value)) || this.product.variants[0];
      this.selectedOptions = [...selected.options];
      this.bindProductSelect();
      this.bindPreview();
      this.bindPreviewControls();
      this.bindProperties();
      this.bindVariants();
      this.bindContourProofSubmission();
      this.hideLegacyLockerPhoneField();
      this.updateVariant();
    }

    disconnectedCallback() {
      window.clearTimeout(this.vaultPreviewTimer);
      window.clearTimeout(this.contourRenderTimer);
      window.clearTimeout(this.contourFormDataTimer);
      if (this.vaultObserver) this.vaultObserver.disconnect();
      if (this.previewBlobUrl) URL.revokeObjectURL(this.previewBlobUrl);
      if (this.contourBaseOwned && this.contourBaseSource) URL.revokeObjectURL(this.contourBaseSource);
    }

    hideLegacyLockerPhoneField() {
      const appBlock = this.querySelector('.fp-app-block');
      if (!appBlock) return;
      const hideField = () => {
        const legacyInput = [...appBlock.querySelectorAll('input, textarea')].find((field) => {
          const placeholder = (field.getAttribute('placeholder') || '').replace(/\D/g, '');
          return placeholder === '3612107199';
        });
        if (legacyInput) {
          let wrapper = legacyInput.parentElement;
          for (let depth = 0; wrapper && depth < 4; depth += 1, wrapper = wrapper.parentElement) {
            const copy = wrapper.textContent.replace(/\s+/g, ' ').trim().toLowerCase();
            if (copy.includes('phone number') && copy.includes('smart locker')) {
              wrapper.style.setProperty('display', 'none', 'important');
              return true;
            }
          }
          legacyInput.style.setProperty('display', 'none', 'important');
          return true;
        }
        const candidates = appBlock.querySelectorAll('label, legend, p, span, div');
        for (const candidate of candidates) {
          const copy = candidate.textContent.replace(/\s+/g, ' ').trim().toLowerCase();
          if (!copy.startsWith('enter phone number for 24/7 smart locker pickup')) continue;
          const label = candidate.closest('label');
          if (label) { label.style.display = 'none'; return true; }
          candidate.style.display = 'none';
          const nextField = candidate.nextElementSibling;
          if (nextField && nextField.matches('input, textarea')) nextField.style.display = 'none';
          return true;
        }
        return false;
      };
      if (hideField()) return;
      const observer = new MutationObserver(() => { if (hideField()) observer.disconnect(); });
      observer.observe(appBlock, { childList: true, subtree: true });
      window.setTimeout(() => observer.disconnect(), 30000);
    }

    bindProductSelect() {
      const select = this.querySelector('[data-product-select]');
      if (!select) return;
      select.addEventListener('change', () => { if (select.value) window.location.href = select.value; });
    }

    bindPreview() {
      if (!this.previewStage) return;
      this.previewStage.addEventListener('click', () => this.openVaultPicker('vault'));
      this.querySelector('[data-preview-vault-action]')?.addEventListener('click', () => this.openVaultPicker('vault'));
      this.querySelector('[data-preview-upload-action]')?.addEventListener('click', () => this.openVaultPicker('upload'));
      this.vaultObserver = new MutationObserver(() => this.queueVaultPreviewSync());
      this.vaultObserver.observe(this, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['hidden', 'src', 'data-file-id', 'data-fp-artwork-source'],
      });
      this.queueVaultPreviewSync();
      this.vaultPreviewTimer = window.setTimeout(() => this.syncVaultPreview(), 1200);
    }

    bindPreviewControls() {
      const zoomOut = this.querySelector('[data-preview-zoom-out]');
      const zoomIn = this.querySelector('[data-preview-zoom-in]');
      const reset = this.querySelector('[data-preview-reset]');
      const renderZoom = () => {
        if (!this.preview) return;
        this.preview.style.setProperty('--fp-preview-scale', String(this.previewScale));
        if (this.previewZoomOutput) this.previewZoomOutput.textContent = `${Math.round(this.previewScale * 100)}%`;
      };
      const setZoom = (value) => {
        this.previewScale = Math.min(1.35, Math.max(.75, Math.round(value * 100) / 100));
        renderZoom();
      };

      zoomOut?.addEventListener('click', () => setZoom(this.previewScale - .1));
      zoomIn?.addEventListener('click', () => setZoom(this.previewScale + .1));
      reset?.addEventListener('click', () => setZoom(1));

      if (!this.borderSizeInput) {
        renderZoom();
        return;
      }
      const renderBorderSize = () => {
        const minimum = Number(this.borderSizeInput.min);
        const stickerSize = this.getStickerSizeInches();
        const scaledMaximum = Math.min(.5, Math.max(.25, Math.round(stickerSize * .1 / .025) * .025));
        this.borderSizeInput.max = String(scaledMaximum);
        const maximum = Number(this.borderSizeInput.max);
        const rawValue = Number(this.borderSizeInput.value);
        const value = Number.isFinite(rawValue) ? Math.min(rawValue, maximum) : .125;
        if (value !== rawValue) this.borderSizeInput.value = String(value);
        const enabled = value > 0;
        const progress = ((value - minimum) / (maximum - minimum)) * 100;
        const renderedArtSize = Math.max(1, this.previewImage?.getBoundingClientRect().width || this.preview?.getBoundingClientRect().width || 440);
        const outlinePixels = Math.max(0, Math.round(renderedArtSize * value / stickerSize));
        const outlineLabel = `${value.toFixed(3).replace(/0+$/, '').replace(/\.$/, '')} in`;
        const outlinePercent = value / stickerSize * 100;
        this.borderInput.value = enabled ? 'Yes' : 'No';
        this.preview.classList.toggle('has-border', enabled);
        this.preview.classList.toggle('no-border', !enabled);
        this.style.setProperty('--fp-cloud-border-size', `${outlinePixels}px`);
        this.borderSizeInput.style.setProperty('--fp-range-progress', `${progress}%`);
        if (this.borderSizeOutput) this.borderSizeOutput.textContent = outlineLabel;
        if (this.borderSizeProperty) this.borderSizeProperty.value = outlineLabel;
        if (this.outlineProportionProperty) this.outlineProportionProperty.value = `${outlinePercent.toFixed(2)}% of ${stickerSize} in sticker size`;
        this.updateSummary();
        this.queueContourRender();
      };
      this.renderBorderSize = renderBorderSize;
      this.borderSizeInput.addEventListener('input', renderBorderSize);
      renderBorderSize();
      renderZoom();
    }

    queueVaultPreviewSync() {
      if (this.vaultSyncQueued) return;
      this.vaultSyncQueued = true;
      window.requestAnimationFrame(() => {
        this.vaultSyncQueued = false;
        this.syncVaultPreview();
      });
    }

    vaultPicker() {
      const productId = String(this.product.id);
      const candidates = [...document.querySelectorAll('[data-fp-pro-vault-picker]')];
      return candidates.find((root) => root.dataset.productId === productId && (this.contains(root) || root.closest('form') === this.form)) ||
        candidates.find((root) => root.dataset.productId === productId) || null;
    }

    openVaultPicker(source = 'vault') {
      const root = this.vaultPicker();
      if (!root) {
        this.previewCopy.textContent = 'Pro Vault is loading…';
        this.vaultPreviewTimer = window.setTimeout(() => this.syncVaultPreview(), 600);
        return;
      }
      root.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (root.dataset.authenticated !== 'true') {
        root.querySelector('a')?.focus({ preventScroll: true });
        return;
      }
      const hasSelection = root.dataset.fpArtworkSource === 'pro-vault';
      if (hasSelection && source === 'vault') {
        root.querySelector('[data-fp-vault-change]')?.click();
        return;
      }
      const sourceButton = root.querySelector(`[data-fp-vault-source="${source}"]:not([disabled])`);
      sourceButton?.click();
      window.requestAnimationFrame(() => {
        if (source === 'upload') root.querySelector('[data-fp-vault-upload-input]:not([disabled])')?.focus({ preventScroll: true });
        else root.querySelector('[data-fp-vault-files] input:not([disabled]), [data-fp-vault-files] button:not([disabled])')?.focus({ preventScroll: true });
      });
    }

    syncVaultPreview() {
      const root = this.vaultPicker();
      const selected = root?.dataset.fpArtworkSource === 'pro-vault'
        ? [...root.querySelectorAll('[data-fp-vault-selected-item]')].find((item) => !item.hidden)
        : null;
      if (!selected) {
        if (this.vaultPreviewKey) this.clearVaultPreview();
        return;
      }

      const name = selected.querySelector('[data-fp-vault-selected-name]')?.textContent?.trim() || 'Artwork attached';
      const detail = selected.querySelector('[data-fp-vault-selected-meta]')?.textContent?.trim() || '';
      const thumb = selected.querySelector('[data-fp-vault-selected-thumb]');
      const fileId = thumb?.dataset.fileId || name;
      const extension = (detail.split('·').pop()?.trim() || name.split('.').pop() || 'FILE').toUpperCase();
      const key = `${fileId}:${name}:${detail}`;
      if (key !== this.vaultPreviewKey) {
        this.previewRequest += 1;
        this.vaultPreviewKey = key;
        this.vaultPreviewSource = '';
        this.showVaultDocument(name, extension, ['JPG', 'JPEG', 'PNG'].includes(extension) ? 'Preparing secure preview…' : 'Preview unavailable — this file is still attached.');
      }

      const thumbImage = thumb?.querySelector('img');
      const source = thumbImage?.currentSrc || thumbImage?.src || '';
      if (source && source !== this.vaultPreviewSource && ['JPG', 'JPEG', 'PNG'].includes(extension)) {
        this.vaultPreviewSource = source;
        this.applyVaultImage(source, name, key);
      }
    }

    clearVaultPreview() {
      this.previewRequest += 1;
      this.contourRenderRequest += 1;
      this.vaultPreviewKey = '';
      this.vaultPreviewSource = '';
      if (this.previewBlobUrl) URL.revokeObjectURL(this.previewBlobUrl);
      if (this.contourBaseOwned && this.contourBaseSource) URL.revokeObjectURL(this.contourBaseSource);
      this.previewBlobUrl = '';
      this.contourBaseSource = '';
      this.contourBaseOwned = false;
      this.contourFileName = '';
      this.contourProofBlob = null;
      this.contourProofSignature = '';
      this.contourProofUpload = null;
      this.previewImage.removeAttribute('src');
      this.previewImage.hidden = true;
      this.previewDocument.hidden = true;
      this.previewPlaceholder.hidden = false;
      this.preview.hidden = true;
      if (this.previewStart) this.previewStart.hidden = false;
      this.preview.classList.remove('has-art', 'has-document', 'has-smart-contour', 'is-contour-processing');
      this.previewCopy.textContent = 'Waiting for artwork';
      this.previewFileName.textContent = 'PNG · JPG · 300 DPI';
    }

    showVaultDocument(name, extension, status) {
      this.contourRenderRequest += 1;
      if (this.previewBlobUrl) URL.revokeObjectURL(this.previewBlobUrl);
      if (this.contourBaseOwned && this.contourBaseSource) URL.revokeObjectURL(this.contourBaseSource);
      this.previewBlobUrl = '';
      this.contourBaseSource = '';
      this.contourBaseOwned = false;
      this.contourProofBlob = null;
      this.contourProofSignature = '';
      this.contourProofUpload = null;
      this.previewImage.removeAttribute('src');
      this.previewImage.hidden = true;
      this.previewPlaceholder.hidden = true;
      this.previewDocument.hidden = false;
      this.preview.hidden = false;
      if (this.previewStart) this.previewStart.hidden = true;
      this.preview.classList.remove('has-art', 'has-smart-contour', 'is-contour-processing');
      this.preview.classList.add('has-document');
      this.previewDocumentType.textContent = extension || 'FILE';
      this.previewDocumentName.textContent = name;
      this.previewDocumentStatus.textContent = status;
      this.previewCopy.textContent = 'Artwork attached through Pro Vault';
      this.previewFileName.textContent = name;
    }

    async applyVaultImage(source, name, key) {
      const request = ++this.previewRequest;
      let previewSource = source;
      let generated = false;
      if (this.isGlowProduct || this.isCrystalProduct || this.isGlitterProduct || this.isHolographicProduct || this.isEggshellProduct) {
        try {
          const response = await fetch(source, { credentials: 'omit' });
          if (!response.ok) throw new Error('Preview file could not be loaded.');
          const blob = await response.blob();
          const file = new File([blob], name, { type: blob.type || 'image/png' });
          previewSource = this.isGlowProduct ? await this.createGlowPreview(file) :
            this.isCrystalProduct ? await this.createCrystalPreview(file, this.dataset.crystalTexture, 0.94, 0.11, 0.34) :
              this.isGlitterProduct ? await this.createCrystalPreview(file, this.dataset.glitterTexture, 0.92, 0.13, 1) :
                this.isHolographicProduct ? await this.createCrystalPreview(file, this.dataset.holographicTexture, 0.94, 0.12, 0.86) :
                  await this.createEggshellPreview(file, this.dataset.eggshellTexture);
          generated = previewSource.startsWith('blob:');
        } catch (_) {
          previewSource = source;
        }
      }
      if (request !== this.previewRequest || key !== this.vaultPreviewKey) {
        if (generated) URL.revokeObjectURL(previewSource);
        return;
      }
      const probe = new Image();
      probe.onload = async () => {
        if (request !== this.previewRequest || key !== this.vaultPreviewKey) {
          if (generated) URL.revokeObjectURL(previewSource);
          return;
        }
        if (this.contourBaseOwned && this.contourBaseSource && this.contourBaseSource !== previewSource) URL.revokeObjectURL(this.contourBaseSource);
        if (this.previewBlobUrl) URL.revokeObjectURL(this.previewBlobUrl);
        this.previewBlobUrl = '';
        this.contourBaseSource = previewSource;
        this.contourBaseOwned = generated;
        this.contourFileName = name;
        this.contourProofBlob = null;
        this.contourProofSignature = '';
        this.contourProofUpload = null;
        this.showPreviewImage(previewSource, name, false);
        try {
          await this.renderContourPreviewNow(request, key);
        } catch (_) {
          this.preview.classList.remove('has-smart-contour', 'is-contour-processing');
          if (this.contourStatus) this.contourStatus.textContent = 'Transparent PNG traces best · Production proof required';
        }
      };
      probe.onerror = () => {
        if (request !== this.previewRequest || key !== this.vaultPreviewKey) return;
        if (generated) URL.revokeObjectURL(previewSource);
        const extension = name.split('.').pop()?.toUpperCase() || 'FILE';
        this.showVaultDocument(name, extension, 'Preview unavailable — this file is still attached.');
      };
      probe.src = previewSource;
    }

    createGlowPreview(file) {
      return new Promise((resolve) => {
        const sourceUrl = URL.createObjectURL(file);
        const image = new Image();
        image.onload = () => {
          const maxSide = 1400;
          const scale = Math.min(1, maxSide / Math.max(image.naturalWidth, image.naturalHeight));
          const canvas = document.createElement('canvas');
          canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
          canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
          const context = canvas.getContext('2d', { willReadFrequently: true });
          context.drawImage(image, 0, 0, canvas.width, canvas.height);
          const pixels = context.getImageData(0, 0, canvas.width, canvas.height);
          for (let index = 0; index < pixels.data.length; index += 4) {
            const red = pixels.data[index];
            const green = pixels.data[index + 1];
            const blue = pixels.data[index + 2];
            const alpha = pixels.data[index + 3];
            const brightest = Math.max(red, green, blue);
            const darkest = Math.min(red, green, blue);
            if (alpha > 8 && brightest > 220 && darkest > 195 && brightest - darkest < 38) {
              const strength = Math.min(1, (brightest - 205) / 50);
              pixels.data[index] = Math.round(red + (145 - red) * strength);
              pixels.data[index + 1] = Math.round(green + (255 - green) * strength);
              pixels.data[index + 2] = Math.round(blue + (0 - blue) * strength);
            } else if (alpha > 8) {
              const glowSeep = 0.1;
              pixels.data[index] = Math.round(red * (1 - glowSeep) + 145 * glowSeep);
              pixels.data[index + 1] = Math.round(green * (1 - glowSeep) + 255 * glowSeep);
              pixels.data[index + 2] = Math.round(blue * (1 - glowSeep));
              pixels.data[index + 3] = Math.round(alpha * 0.92);
            }
          }
          context.putImageData(pixels, 0, 0);
          canvas.toBlob((blob) => {
            URL.revokeObjectURL(sourceUrl);
            resolve(blob ? URL.createObjectURL(blob) : URL.createObjectURL(file));
          }, 'image/png');
        };
        image.onerror = () => { URL.revokeObjectURL(sourceUrl); resolve(URL.createObjectURL(file)); };
        image.src = sourceUrl;
      });
    }

    async createCrystalPreview(file, textureUrl, whiteStrength = 0.94, colorStrength = 0.11, tileRatio = 0.34) {
      const sourceUrl = URL.createObjectURL(file);
      try {
        const [artwork, texture] = await Promise.all([this.loadPreviewImage(sourceUrl), this.loadPreviewImage(textureUrl, true)]);
        const maxSide = 1400;
        const scale = Math.min(1, maxSide / Math.max(artwork.naturalWidth, artwork.naturalHeight));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(artwork.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(artwork.naturalHeight * scale));
        const context = canvas.getContext('2d', { willReadFrequently: true });
        context.drawImage(artwork, 0, 0, canvas.width, canvas.height);
        const artPixels = context.getImageData(0, 0, canvas.width, canvas.height);
        const textureCanvas = document.createElement('canvas');
        textureCanvas.width = canvas.width;
        textureCanvas.height = canvas.height;
        const textureContext = textureCanvas.getContext('2d', { willReadFrequently: true });
        const tileSize = Math.max(180, Math.min(1400, Math.round(Math.min(canvas.width, canvas.height) * tileRatio)));
        for (let y = 0; y < canvas.height; y += tileSize) {
          for (let x = 0; x < canvas.width; x += tileSize) textureContext.drawImage(texture, x, y, tileSize, tileSize);
        }
        const texturePixels = textureContext.getImageData(0, 0, canvas.width, canvas.height);
        for (let index = 0; index < artPixels.data.length; index += 4) {
          const red = artPixels.data[index];
          const green = artPixels.data[index + 1];
          const blue = artPixels.data[index + 2];
          const alpha = artPixels.data[index + 3];
          if (alpha <= 8) continue;
          const brightest = Math.max(red, green, blue);
          const darkest = Math.min(red, green, blue);
          const isWhite = brightest > 220 && darkest > 195 && brightest - darkest < 38;
          const materialStrength = isWhite ? whiteStrength : colorStrength;
          artPixels.data[index] = Math.round(red * (1 - materialStrength) + texturePixels.data[index] * materialStrength);
          artPixels.data[index + 1] = Math.round(green * (1 - materialStrength) + texturePixels.data[index + 1] * materialStrength);
          artPixels.data[index + 2] = Math.round(blue * (1 - materialStrength) + texturePixels.data[index + 2] * materialStrength);
          if (!isWhite) artPixels.data[index + 3] = Math.round(alpha * 0.94);
        }
        context.putImageData(artPixels, 0, 0);
        return await new Promise((resolve) => canvas.toBlob((blob) => resolve(blob ? URL.createObjectURL(blob) : URL.createObjectURL(file)), 'image/png'));
      } catch (error) {
        return URL.createObjectURL(file);
      } finally {
        setTimeout(() => URL.revokeObjectURL(sourceUrl), 1000);
      }
    }

    async createEggshellPreview(file, textureUrl) {
      const sourceUrl = URL.createObjectURL(file);
      try {
        const [artwork, texture] = await Promise.all([this.loadPreviewImage(sourceUrl), this.loadPreviewImage(textureUrl, true)]);
        const maxSide = 1400;
        const scale = Math.min(1, maxSide / Math.max(artwork.naturalWidth, artwork.naturalHeight));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(artwork.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(artwork.naturalHeight * scale));
        const context = canvas.getContext('2d', { willReadFrequently: true });
        context.drawImage(artwork, 0, 0, canvas.width, canvas.height);
        const artPixels = context.getImageData(0, 0, canvas.width, canvas.height);
        const textureCanvas = document.createElement('canvas');
        textureCanvas.width = canvas.width;
        textureCanvas.height = canvas.height;
        const textureContext = textureCanvas.getContext('2d', { willReadFrequently: true });
        const tileSize = Math.max(260, Math.round(Math.min(canvas.width, canvas.height) * 0.82));
        for (let y = 0; y < canvas.height; y += tileSize) {
          for (let x = 0; x < canvas.width; x += tileSize) textureContext.drawImage(texture, x, y, tileSize, tileSize);
        }
        const texturePixels = textureContext.getImageData(0, 0, canvas.width, canvas.height);
        for (let index = 0; index < artPixels.data.length; index += 4) {
          const red = artPixels.data[index];
          const green = artPixels.data[index + 1];
          const blue = artPixels.data[index + 2];
          const alpha = artPixels.data[index + 3];
          if (alpha <= 8) continue;
          const textureLuma = (texturePixels.data[index] * 0.299 + texturePixels.data[index + 1] * 0.587 + texturePixels.data[index + 2] * 0.114) / 255;
          const crackDepth = Math.min(0.58, 0.035 + Math.max(0, 0.94 - textureLuma) * 6.2);
          const paperShade = 1 - crackDepth;
          const brightest = Math.max(red, green, blue);
          const darkest = Math.min(red, green, blue);
          const isWhite = brightest > 220 && darkest > 195 && brightest - darkest < 38;
          if (isWhite) {
            const warmPaper = Math.round(250 * paperShade);
            artPixels.data[index] = warmPaper;
            artPixels.data[index + 1] = Math.round(248 * paperShade);
            artPixels.data[index + 2] = Math.round(242 * paperShade);
          } else {
            artPixels.data[index] = Math.round(red * paperShade);
            artPixels.data[index + 1] = Math.round(green * paperShade);
            artPixels.data[index + 2] = Math.round(blue * paperShade);
          }
        }
        context.putImageData(artPixels, 0, 0);
        return await new Promise((resolve) => canvas.toBlob((blob) => resolve(blob ? URL.createObjectURL(blob) : URL.createObjectURL(file)), 'image/png'));
      } catch (error) {
        return URL.createObjectURL(file);
      } finally {
        setTimeout(() => URL.revokeObjectURL(sourceUrl), 1000);
      }
    }

    loadPreviewImage(url, anonymous = false) {
      return new Promise((resolve, reject) => {
        const image = new Image();
        if (anonymous) image.crossOrigin = 'anonymous';
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = url;
      });
    }

    getStickerSizeInches() {
      const sizeOption = this.selectedOptions?.find((value) => /(\d+(?:\.\d+)?)\s*(?:in|inch)/i.test(String(value)));
      const match = String(sizeOption || '').match(/(\d+(?:\.\d+)?)/);
      const size = Number(match?.[1]);
      return Number.isFinite(size) && size > 0 ? size : 2;
    }

    currentContourSignature() {
      const size = this.getStickerSizeInches();
      const outline = Number(this.borderSizeInput?.value || 0);
      return `${this.vaultPreviewKey}|${this.shapeInput?.value || 'Die Cut'}|${size}|${outline.toFixed(3)}`;
    }

    showPreviewImage(source, name, smartContour) {
      this.previewImage.src = source;
      this.previewImage.alt = `Preview of ${name}`;
      this.previewImage.hidden = false;
      this.previewDocument.hidden = true;
      this.previewPlaceholder.hidden = true;
      this.preview.hidden = false;
      if (this.previewStart) this.previewStart.hidden = true;
      this.preview.classList.remove('has-document');
      this.preview.classList.toggle('has-smart-contour', Boolean(smartContour));
      this.preview.classList.add('has-art');
      this.previewCopy.textContent = smartContour
        ? (this.shapeInput?.value === 'Die Cut' ? 'Smart contour ready' : `${this.shapeInput?.value || 'Shape'} proof ready`)
        : 'Artwork attached through Pro Vault';
      this.previewFileName.textContent = name;
    }

    queueContourRender() {
      if (!this.contourBaseSource || !this.preview?.classList.contains('has-art')) return;
      window.clearTimeout(this.contourRenderTimer);
      this.preview.classList.add('is-contour-processing');
      if (this.contourStatus) this.contourStatus.textContent = this.shapeInput?.value === 'Die Cut' ? 'Scanning and smoothing contour…' : 'Fitting the full artwork inside the selected shape…';
      this.contourRenderTimer = window.setTimeout(() => {
        this.renderContourPreviewNow(this.previewRequest, this.vaultPreviewKey).catch(() => {
          this.preview.classList.remove('is-contour-processing');
          if (this.contourStatus) this.contourStatus.textContent = 'Transparent PNG traces best · Production proof required';
        });
      }, 180);
    }

    async renderContourPreviewNow(previewRequest = this.previewRequest, key = this.vaultPreviewKey) {
      if (!this.contourBaseSource) return null;
      const renderRequest = ++this.contourRenderRequest;
      const signature = this.currentContourSignature();
      if (signature === this.contourProofSignature && this.contourProofBlob) {
        this.preview.classList.remove('is-contour-processing');
        return this.contourProofBlob;
      }
      this.preview.classList.add('is-contour-processing');
      const shape = this.shapeInput?.value || 'Die Cut';
      if (this.contourStatus) this.contourStatus.textContent = shape === 'Die Cut' ? 'Scanning and smoothing contour…' : `Fitting full artwork inside ${shape.toLowerCase()}…`;
      const result = shape === 'Die Cut'
        ? await this.createSmartContourProof(this.contourBaseSource)
        : await this.createShapeProof(this.contourBaseSource, shape);
      if (renderRequest !== this.contourRenderRequest || previewRequest !== this.previewRequest || key !== this.vaultPreviewKey) return null;

      if (this.previewBlobUrl) URL.revokeObjectURL(this.previewBlobUrl);
      this.previewBlobUrl = URL.createObjectURL(result.blob);
      this.contourProofBlob = result.blob;
      this.contourProofSignature = signature;
      this.contourProofUpload = null;
      this.showPreviewImage(this.previewBlobUrl, this.contourFileName, true);
      this.preview.classList.remove('is-contour-processing');
      const outline = Number(this.borderSizeInput?.value || 0).toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
      const size = this.getStickerSizeInches();
      if (shape === 'Die Cut') {
        if (this.contourMethodProperty) this.contourMethodProperty.value = `Smart ${result.mode} contour · rounded offset`;
        if (this.contourStatus) this.contourStatus.textContent = `Smart contour ready · ${outline || 0} in cloud border at ${size} in`;
      } else {
        if (this.contourMethodProperty) this.contourMethodProperty.value = `${shape} cut · full artwork fitted inside shape`;
        if (this.contourStatus) this.contourStatus.textContent = `${shape} proof ready · full artwork fitted · ${outline || 0} in cloud border`;
      }
      this.syncContourLineProperties();
      return result.blob;
    }

    async createShapeProof(source, shape) {
      const response = await fetch(source, { credentials: 'omit' });
      if (!response.ok) throw new Error('Artwork preview could not be fitted.');
      const sourceBlob = await response.blob();
      const localUrl = URL.createObjectURL(sourceBlob);
      let artwork;
      try {
        artwork = await this.loadPreviewImage(localUrl);
      } finally {
        URL.revokeObjectURL(localUrl);
      }

      const outputSize = window.matchMedia('(max-width: 749px)').matches ? 900 : 1200;
      const sourceScale = Math.min(1, outputSize / Math.max(artwork.naturalWidth, artwork.naturalHeight));
      const sourceCanvas = document.createElement('canvas');
      sourceCanvas.width = Math.max(1, Math.round(artwork.naturalWidth * sourceScale));
      sourceCanvas.height = Math.max(1, Math.round(artwork.naturalHeight * sourceScale));
      const sourceContext = sourceCanvas.getContext('2d', { willReadFrequently: true });
      sourceContext.imageSmoothingEnabled = true;
      sourceContext.imageSmoothingQuality = 'high';
      sourceContext.drawImage(artwork, 0, 0, sourceCanvas.width, sourceCanvas.height);

      const pixels = sourceContext.getImageData(0, 0, sourceCanvas.width, sourceCanvas.height);
      let left = sourceCanvas.width;
      let top = sourceCanvas.height;
      let right = -1;
      let bottom = -1;
      for (let y = 0; y < sourceCanvas.height; y += 1) {
        for (let x = 0; x < sourceCanvas.width; x += 1) {
          if (pixels.data[(y * sourceCanvas.width + x) * 4 + 3] <= 20) continue;
          left = Math.min(left, x);
          right = Math.max(right, x);
          top = Math.min(top, y);
          bottom = Math.max(bottom, y);
        }
      }
      if (right < left || bottom < top) throw new Error('No printable artwork was found.');

      const cropWidth = right - left + 1;
      const cropHeight = bottom - top + 1;
      const stickerSize = this.getStickerSizeInches();
      const outlineInches = Number(this.borderSizeInput?.value || 0);
      const borderPixels = Math.max(0, Math.round(outputSize * outlineInches / stickerSize));
      const safetyInset = Math.max(3, borderPixels);
      const output = document.createElement('canvas');
      output.width = outputSize;
      output.height = outputSize;
      const outputContext = output.getContext('2d');
      outputContext.imageSmoothingEnabled = true;
      outputContext.imageSmoothingQuality = 'high';

      outputContext.fillStyle = '#fff';
      outputContext.beginPath();
      if (shape === 'Circle') outputContext.arc(outputSize / 2, outputSize / 2, outputSize / 2 - 1, 0, Math.PI * 2);
      else outputContext.rect(1, 1, outputSize - 2, outputSize - 2);
      outputContext.fill();

      let fitScale;
      if (shape === 'Circle') {
        const usableDiameter = Math.max(1, outputSize - safetyInset * 2);
        fitScale = usableDiameter / Math.max(1, Math.hypot(cropWidth, cropHeight));
      } else {
        const usableSide = Math.max(1, outputSize - safetyInset * 2);
        fitScale = Math.min(usableSide / cropWidth, usableSide / cropHeight);
      }
      const drawWidth = cropWidth * fitScale;
      const drawHeight = cropHeight * fitScale;
      const drawX = (outputSize - drawWidth) / 2;
      const drawY = (outputSize - drawHeight) / 2;

      outputContext.save();
      outputContext.beginPath();
      if (shape === 'Circle') outputContext.arc(outputSize / 2, outputSize / 2, outputSize / 2 - 1, 0, Math.PI * 2);
      else outputContext.rect(1, 1, outputSize - 2, outputSize - 2);
      outputContext.clip();
      outputContext.drawImage(sourceCanvas, left, top, cropWidth, cropHeight, drawX, drawY, drawWidth, drawHeight);
      outputContext.restore();

      const blob = await new Promise((resolve, reject) => output.toBlob((value) => value ? resolve(value) : reject(new Error('Shape proof could not be created.')), 'image/png'));
      return { blob, mode: `${shape.toLowerCase()}-fit`, outlinePixels: borderPixels };
    }

    async createSmartContourProof(source) {
      const response = await fetch(source, { credentials: 'omit' });
      if (!response.ok) throw new Error('Artwork preview could not be scanned.');
      const sourceBlob = await response.blob();
      const localUrl = URL.createObjectURL(sourceBlob);
      let artwork;
      try {
        artwork = await this.loadPreviewImage(localUrl);
      } finally {
        URL.revokeObjectURL(localUrl);
      }

      const maxSide = window.matchMedia('(max-width: 749px)').matches ? 900 : 1200;
      const scale = Math.min(1, maxSide / Math.max(artwork.naturalWidth, artwork.naturalHeight));
      const sourceCanvas = document.createElement('canvas');
      sourceCanvas.width = Math.max(1, Math.round(artwork.naturalWidth * scale));
      sourceCanvas.height = Math.max(1, Math.round(artwork.naturalHeight * scale));
      const sourceContext = sourceCanvas.getContext('2d', { willReadFrequently: true });
      sourceContext.imageSmoothingEnabled = true;
      sourceContext.imageSmoothingQuality = 'high';
      sourceContext.drawImage(artwork, 0, 0, sourceCanvas.width, sourceCanvas.height);
      const pixels = sourceContext.getImageData(0, 0, sourceCanvas.width, sourceCanvas.height);
      const alphaResult = this.deriveSmartAlpha(pixels, sourceCanvas.width, sourceCanvas.height);
      const { alpha, bounds, mode } = alphaResult;
      const cropWidth = bounds.right - bounds.left + 1;
      const cropHeight = bounds.bottom - bounds.top + 1;
      const stickerSize = this.getStickerSizeInches();
      const outlineInches = Number(this.borderSizeInput?.value || 0);
      const outlinePixels = Math.max(0, Math.round(Math.max(cropWidth, cropHeight) * outlineInches / stickerSize));
      const padding = Math.max(8, outlinePixels + 8);
      const outputWidth = cropWidth + padding * 2;
      const outputHeight = cropHeight + padding * 2;
      const output = document.createElement('canvas');
      output.width = outputWidth;
      output.height = outputHeight;
      const outputContext = output.getContext('2d', { willReadFrequently: true });

      if (outlinePixels > 0) {
        const mask = new Uint8Array(outputWidth * outputHeight);
        for (let y = 0; y < cropHeight; y += 1) {
          const sourceRow = (bounds.top + y) * sourceCanvas.width + bounds.left;
          const targetRow = (padding + y) * outputWidth + padding;
          for (let x = 0; x < cropWidth; x += 1) mask[targetRow + x] = alpha[sourceRow + x] > 20 ? 1 : 0;
        }
        const distance = this.euclideanDistanceTransform(mask, outputWidth, outputHeight);
        const outlineImage = outputContext.createImageData(outputWidth, outputHeight);
        const softEdge = .85;
        for (let index = 0; index < distance.length; index += 1) {
          const edge = outlinePixels + softEdge - distance[index];
          if (edge <= 0) continue;
          outlineImage.data[index * 4] = 255;
          outlineImage.data[index * 4 + 1] = 255;
          outlineImage.data[index * 4 + 2] = 255;
          outlineImage.data[index * 4 + 3] = Math.round(Math.min(1, edge) * 255);
        }
        outputContext.putImageData(outlineImage, 0, 0);
      }

      const croppedArtwork = outputContext.createImageData(cropWidth, cropHeight);
      for (let y = 0; y < cropHeight; y += 1) {
        for (let x = 0; x < cropWidth; x += 1) {
          const sourceIndex = ((bounds.top + y) * sourceCanvas.width + bounds.left + x);
          const targetIndex = y * cropWidth + x;
          croppedArtwork.data[targetIndex * 4] = pixels.data[sourceIndex * 4];
          croppedArtwork.data[targetIndex * 4 + 1] = pixels.data[sourceIndex * 4 + 1];
          croppedArtwork.data[targetIndex * 4 + 2] = pixels.data[sourceIndex * 4 + 2];
          croppedArtwork.data[targetIndex * 4 + 3] = alpha[sourceIndex];
        }
      }
      const artworkCanvas = document.createElement('canvas');
      artworkCanvas.width = cropWidth;
      artworkCanvas.height = cropHeight;
      artworkCanvas.getContext('2d').putImageData(croppedArtwork, 0, 0);
      outputContext.drawImage(artworkCanvas, padding, padding);
      const blob = await new Promise((resolve, reject) => output.toBlob((value) => value ? resolve(value) : reject(new Error('Contour proof could not be created.')), 'image/png'));
      return { blob, mode, outlinePixels };
    }

    deriveSmartAlpha(pixels, width, height) {
      const total = width * height;
      const alpha = new Uint8ClampedArray(total);
      let transparent = 0;
      for (let index = 0; index < total; index += 1) {
        alpha[index] = pixels.data[index * 4 + 3];
        if (alpha[index] < 245) transparent += 1;
      }
      let mode = 'alpha';
      if (transparent / total < .005) {
        const samples = this.cornerColorSamples(pixels, width, height);
        const visited = new Uint8Array(total);
        const queue = new Int32Array(total);
        let head = 0;
        let tail = 0;
        const toleranceSquared = 46 * 46;
        const isBackground = (index) => {
          const red = pixels.data[index * 4];
          const green = pixels.data[index * 4 + 1];
          const blue = pixels.data[index * 4 + 2];
          return samples.some((sample) => {
            const dr = red - sample[0];
            const dg = green - sample[1];
            const db = blue - sample[2];
            return dr * dr + dg * dg + db * db <= toleranceSquared;
          });
        };
        const add = (index) => {
          if (visited[index] || !isBackground(index)) return;
          visited[index] = 1;
          queue[tail++] = index;
        };
        for (let x = 0; x < width; x += 1) { add(x); add((height - 1) * width + x); }
        for (let y = 1; y < height - 1; y += 1) { add(y * width); add(y * width + width - 1); }
        while (head < tail) {
          const index = queue[head++];
          const x = index % width;
          const y = Math.floor(index / width);
          if (x > 0) add(index - 1);
          if (x + 1 < width) add(index + 1);
          if (y > 0) add(index - width);
          if (y + 1 < height) add(index + width);
        }
        const remaining = total - tail;
        if (tail > total * .03 && remaining > total * .025) {
          for (let index = 0; index < total; index += 1) alpha[index] = visited[index] ? 0 : 255;
          mode = 'background-scan';
        }
      }

      let left = width;
      let top = height;
      let right = -1;
      let bottom = -1;
      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          if (alpha[y * width + x] <= 20) continue;
          left = Math.min(left, x);
          right = Math.max(right, x);
          top = Math.min(top, y);
          bottom = Math.max(bottom, y);
        }
      }
      if (right < left || bottom < top) throw new Error('No printable artwork edge was found.');
      return { alpha, mode, bounds: { left, top, right, bottom } };
    }

    cornerColorSamples(pixels, width, height) {
      const sampleSize = Math.max(2, Math.round(Math.min(width, height) * .015));
      const origins = [[0, 0], [width - sampleSize, 0], [0, height - sampleSize], [width - sampleSize, height - sampleSize]];
      return origins.map(([startX, startY]) => {
        let red = 0;
        let green = 0;
        let blue = 0;
        let count = 0;
        for (let y = startY; y < startY + sampleSize; y += 1) {
          for (let x = startX; x < startX + sampleSize; x += 1) {
            const index = (y * width + x) * 4;
            red += pixels.data[index];
            green += pixels.data[index + 1];
            blue += pixels.data[index + 2];
            count += 1;
          }
        }
        return [red / count, green / count, blue / count];
      });
    }

    euclideanDistanceTransform(mask, width, height) {
      const total = width * height;
      const infinity = width + height + 1;
      const diagonal = Math.SQRT2;
      const result = new Float32Array(total);
      for (let index = 0; index < total; index += 1) result[index] = mask[index] ? 0 : infinity;

      for (let y = 0; y < height; y += 1) {
        const row = y * width;
        for (let x = 0; x < width; x += 1) {
          const index = row + x;
          if (result[index] === 0) continue;
          if (x > 0) result[index] = Math.min(result[index], result[index - 1] + 1);
          if (y > 0) {
            result[index] = Math.min(result[index], result[index - width] + 1);
            if (x > 0) result[index] = Math.min(result[index], result[index - width - 1] + diagonal);
            if (x + 1 < width) result[index] = Math.min(result[index], result[index - width + 1] + diagonal);
          }
        }
      }

      for (let y = height - 1; y >= 0; y -= 1) {
        const row = y * width;
        for (let x = width - 1; x >= 0; x -= 1) {
          const index = row + x;
          if (result[index] === 0) continue;
          if (x + 1 < width) result[index] = Math.min(result[index], result[index + 1] + 1);
          if (y + 1 < height) {
            result[index] = Math.min(result[index], result[index + width] + 1);
            if (x > 0) result[index] = Math.min(result[index], result[index + width - 1] + diagonal);
            if (x + 1 < width) result[index] = Math.min(result[index], result[index + width + 1] + diagonal);
          }
        }
      }
      return result;
    }

    syncContourLineProperties() {
      const size = this.getStickerSizeInches();
      const outline = Number(this.borderSizeInput?.value || 0);
      if (this.outlineProportionProperty) this.outlineProportionProperty.value = `${(outline / size * 100).toFixed(2)}% of ${size} in sticker size`;
      if (this.outlineReferenceName) this.outlineReferenceName.value = this.contourProofUpload?.name || '';
      if (this.outlineReferenceVersion) this.outlineReferenceVersion.value = this.contourProofUpload?.versionId || '';
    }

    bindContourProofSubmission() {
      const bindFormDataAfterVault = () => {
        if (this.contourFormDataBound) return;
        const picker = this.vaultPicker();
        if (!picker || picker.dataset.fpVaultInitialized !== 'true') {
          this.contourFormDataTimer = window.setTimeout(bindFormDataAfterVault, 250);
          return;
        }
        this.form.addEventListener('formdata', (event) => this.mergeContourFormData(event.formData));
        this.contourFormDataBound = true;
      };
      bindFormDataAfterVault();

      this.form.addEventListener('submit', (event) => {
        this.syncContourLineProperties();
        if (this.contourSubmitBypass) {
          this.contourSubmitBypass = false;
          return;
        }
        const picker = this.vaultPicker();
        const shouldAttach = picker?.dataset.fpArtworkSource === 'pro-vault' && Boolean(this.contourBaseSource);
        const signature = this.currentContourSignature();
        if (!shouldAttach || this.contourProofUpload?.signature === signature) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.contourProofSubmitting) return;
        this.contourProofSubmitting = true;
        const submitter = event.submitter || this.addButton;
        const oldCopy = this.addButton.textContent;
        this.addButton.disabled = true;
        this.addButton.textContent = 'Preparing contour…';
        this.prepareContourReference(signature).finally(() => {
          this.contourProofSubmitting = false;
          this.addButton.disabled = false;
          this.addButton.textContent = oldCopy;
          this.contourSubmitBypass = true;
          this.form.requestSubmit(submitter);
        });
      }, true);
    }

    mergeContourFormData(formData) {
      if (!this.contourProofUpload?.versionId) return;
      const key = 'properties[_pro_vault_file_versions]';
      let versions = [];
      try { versions = JSON.parse(formData.get(key) || '[]'); } catch (_) { versions = []; }
      if (!versions.includes(this.contourProofUpload.versionId)) versions.push(this.contourProofUpload.versionId);
      formData.set(key, JSON.stringify(versions));
      formData.set('properties[Cloud border reference file]', this.contourProofUpload.name);
      formData.set('properties[_sticker_outline_reference_version]', this.contourProofUpload.versionId);
    }

    async prepareContourReference(signature) {
      try {
        const blob = signature === this.contourProofSignature && this.contourProofBlob
          ? this.contourProofBlob
          : await this.renderContourPreviewNow(this.previewRequest, this.vaultPreviewKey);
        if (!blob) return;
        this.contourProofUpload = await this.uploadContourReference(blob, signature);
        this.syncContourLineProperties();
        if (this.contourStatus) this.contourStatus.textContent = 'Contour reference attached to this order';
      } catch (_) {
        this.contourProofUpload = { signature, failed: true, name: '', versionId: '' };
        this.syncContourLineProperties();
        if (this.contourStatus) this.contourStatus.textContent = 'Cloud border size recorded · Production proof still required';
      }
    }

    async uploadContourReference(blob, signature) {
      const picker = this.vaultPicker();
      if (!picker?.dataset.proxyUrl) throw new Error('Contour upload is unavailable.');
      const outline = Number(this.borderSizeInput?.value || 0).toFixed(3).replace(/0+$/, '').replace(/\.$/, '').replace('.', '_');
      const size = String(this.getStickerSizeInches()).replace('.', '_');
      const baseName = (this.contourFileName || 'sticker-artwork').replace(/\.[^.]+$/, '').replace(/[^a-z0-9_-]+/gi, '-').replace(/^-+|-+$/g, '').slice(0, 80) || 'sticker-artwork';
      const shape = (this.shapeInput?.value || 'die-cut').toLowerCase().replace(/\s+/g, '-');
      const name = `${baseName}-${shape}-cloud-border-${outline}in-at-${size}in.png`;
      const context = { productGid: String(this.product.id || ''), variantGid: String(this.variantInput?.value || '') };
      const session = await this.contourProxyRequest(picker, 'initiate-upload', {
        name,
        mediaType: 'image/png',
        sizeBytes: String(blob.size),
        storageMode: 'ORDER_ONLY',
        ...context,
      });
      const parts = new Array(session.partCount);
      let nextPart = 1;
      const uploadWorker = async () => {
        while (nextPart <= session.partCount) {
          const partNumber = nextPart++;
          const partSize = Number(session.partSizeBytes);
          const chunk = blob.slice((partNumber - 1) * partSize, Math.min(blob.size, partNumber * partSize));
          const target = await this.contourProxyRequest(picker, 'upload-part', { uploadSessionId: session.uploadSessionId, partNumber });
          const response = await fetch(target.url, { method: target.method, headers: target.headers, body: chunk });
          if (!response.ok) throw new Error(`Contour reference part ${partNumber} failed.`);
          const etag = response.headers.get('etag');
          if (!etag) throw new Error('Contour reference upload did not return an ETag.');
          parts[partNumber - 1] = { partNumber, etag };
        }
      };
      await Promise.all(Array.from({ length: Math.min(3, session.partCount) }, () => uploadWorker()));
      const completed = await this.contourProxyRequest(picker, 'complete-upload', { uploadSessionId: session.uploadSessionId, parts });
      return { signature, name: completed.name || name, versionId: completed.versionId };
    }

    async contourProxyRequest(picker, intent, payload) {
      const response = await fetch(picker.dataset.proxyUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ intent, ...payload }),
      });
      const text = await response.text();
      if (!response.ok) throw new Error(text.trim() || `Contour request failed (${response.status}).`);
      return text ? JSON.parse(text) : {};
    }

    bindProperties() {
      this.querySelectorAll('[data-shape]').forEach((button) => button.addEventListener('click', () => {
        this.querySelectorAll('[data-shape]').forEach((item) => item.classList.toggle('is-selected', item === button));
        const value = button.dataset.shape;
        this.shapeInput.value = value;
        this.preview.dataset.shape = value.toLowerCase().replace(' ', '-');
        this.updateSummary();
        this.queueContourRender();
      }));
      this.querySelectorAll('[data-white-border]').forEach((button) => button.addEventListener('click', () => {
        this.querySelectorAll('[data-white-border]').forEach((item) => item.classList.toggle('is-selected', item === button));
        const enabled = button.dataset.whiteBorder === 'Yes';
        this.borderInput.value = enabled ? 'Yes' : 'No';
        this.preview.classList.toggle('has-border', enabled);
        this.preview.classList.toggle('no-border', !enabled);
        if (this.borderSliderWrap) this.borderSliderWrap.classList.toggle('is-disabled', !enabled);
        if (this.borderSizeInput) this.borderSizeInput.disabled = !enabled;
        if (this.renderBorderSize) this.renderBorderSize();
        this.updateSummary();
      }));
    }

    bindVariants() {
      this.querySelectorAll('[data-option-position]').forEach((button) => button.addEventListener('click', () => {
        const position = Number(button.dataset.optionPosition) - 1;
        this.selectedOptions[position] = button.dataset.optionValue;
        this.querySelectorAll(`[data-option-position="${button.dataset.optionPosition}"]`).forEach((item) => item.classList.toggle('is-selected', item === button));
        this.updateVariant();
      }));
    }

    updateVariant() {
      const variant = this.product.variants.find((candidate) => candidate.options.every((value, index) => value === this.selectedOptions[index]));
      if (!variant) { this.addButton.disabled = true; this.addButton.textContent = 'Unavailable'; return; }
      this.variantInput.value = variant.id;
      this.price.textContent = this.formatMoney(variant.price);
      this.addButton.disabled = !variant.available;
      this.addButton.textContent = variant.available ? 'Add to cart →' : 'Sold out';
      this.updateQuantityPrices();
      this.updateSummary();
      if (this.renderBorderSize) this.renderBorderSize();
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    updateQuantityPrices() {
      this.querySelectorAll('[data-quantity-option]').forEach((button) => {
        const position = Number(button.dataset.optionPosition) - 1;
        const options = [...this.selectedOptions];
        options[position] = button.dataset.optionValue;
        const variant = this.product.variants.find((candidate) => candidate.options.every((value, index) => value === options[index]));
        const price = button.querySelector('[data-option-price]');
        if (price) price.textContent = variant ? this.formatMoney(variant.price) : '—';
        button.disabled = !variant || !variant.available;
      });
    }

    updateSummary() {
      const summary = this.querySelector('[data-order-summary]');
      if (!summary) return;
      const selectedLabels = this.selectedOptions.filter(Boolean).join(' · ');
      const borderSize = Number(this.borderSizeInput?.value || 0);
      const outlineValue = borderSize.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
      const borderLabel = borderSize > 0 ? `${outlineValue} in cloud border` : 'no cloud border';
      summary.textContent = `${selectedLabels} · ${this.shapeInput.value} · ${borderLabel}`;
    }

    formatMoney(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: this.dataset.currency || 'USD' }).format(cents / 100);
    }
  });
}
